"use client";

import React, { useState } from "react";
import { MapPin, Mail, Phone, Terminal, Code, Cpu, ArrowRight, CircleCheck, CircleAlert, Loader } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { cn } from "@/lib/utils";
const ContactForm = () => {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control,
    resetForm
  } = useWvcForm();
  const [activeTab, setActiveTab] = useState("new-build");
  if (isSubmitted && !submitError) {
    return <div data-section-id="8404" className="flex flex-col items-center justify-center h-full min-h-[400px] p-8 text-center space-y-6 bg-card border border-border rounded-sm animate-in fade-in zoom-in duration-500">
        <div className="size-16 rounded-sm bg-primary/10 flex items-center justify-center border border-primary/50 shadow-[0_0_30px_-5px_var(--color-primary)]">
          <CircleCheck className="size-8 text-primary" />
        </div>
        <div className="space-y-2">
          <h3 className="text-2xl font-bold tracking-tight text-foreground">Consultation Request Received</h3>
          <p className="text-muted-foreground max-w-md mx-auto">{successMessage}</p>
        </div>
        <Button onClick={resetForm} variant="outline" className="mt-4 border-primary/50 hover:bg-primary/10 hover:text-primary rounded-sm">
          Initialize New Request
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      {}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <div className="flex items-center justify-between mb-6">
          <Label className="text-xs font-mono uppercase tracking-widest text-muted-foreground">
            SELECT_SERVICE_TYPE
          </Label>
        </div>
        <TabsList className="grid w-full grid-cols-2 bg-muted/50 p-1 border border-border rounded-sm">
          <TabsTrigger value="new-build" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground font-mono text-xs tracking-wide rounded-sm">
            NEW_PROJECT
          </TabsTrigger>
          <TabsTrigger value="refactoring" className="data-[state=active]:bg-secondary data-[state=active]:text-secondary-foreground font-mono text-xs tracking-wide rounded-sm">
            AUDIT_COMPLIANCE
          </TabsTrigger>
        </TabsList>

        {}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
          <FormField control={control} name="name" rules={{
          required: "Name is required"
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="font-mono text-xs uppercase text-muted-foreground">var client_name</FormLabel>
                <FormControl>
                  <Input placeholder="Johnathan Smith" {...field} className="bg-background/50 font-mono text-sm border-border focus-visible:border-primary focus-visible:ring-primary/20 rounded-sm" />
                </FormControl>
                <FormMessage />
              </FormItem>} />
          <FormField control={control} name="email" rules={{
          required: "Email is required",
          pattern: {
            value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
            message: "Invalid email format"
          }
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="font-mono text-xs uppercase text-muted-foreground">var contact_email</FormLabel>
                <FormControl>
                  <Input type="email" placeholder="jsmith@mining-corp.com" {...field} className="bg-background/50 font-mono text-sm border-border focus-visible:border-primary focus-visible:ring-primary/20 rounded-sm" />
                </FormControl>
                <FormMessage />
              </FormItem>} />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <FormField control={control} name="phone" render={({
          field
        }) => <FormItem>
                <FormLabel className="font-mono text-xs uppercase text-muted-foreground">var phone_number</FormLabel>
                <FormControl>
                  <Input type="tel" placeholder="+1 (555) 000-0000" {...field} className="bg-background/50 font-mono text-sm border-border focus-visible:border-primary focus-visible:ring-primary/20 rounded-sm" />
                </FormControl>
                <FormMessage />
              </FormItem>} />
          <FormField control={control} name="company" render={({
          field
        }) => <FormItem>
                <FormLabel className="font-mono text-xs uppercase text-muted-foreground">var firm_entity</FormLabel>
                <FormControl>
                  <Input placeholder="Global Resources Inc." {...field} className="bg-background/50 font-mono text-sm border-border focus-visible:border-primary focus-visible:ring-primary/20 rounded-sm" />
                </FormControl>
                <FormMessage />
              </FormItem>} />
        </div>

        {}
        <div className="mt-6">
          <TabsContent value="new-build" className="mt-0 space-y-4 force-mount">
            <FormField control={control} name="project_description" rules={{
            required: "Project details are required"
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel className="font-mono text-xs uppercase text-primary">function deploy_project()</FormLabel>
                  <FormControl>
                    <Textarea placeholder="Describe the geotechnical scope, facility location, and specific impoundment requirements (e.g., Water Balance, Earthworks, EOR)..." className="min-h-[120px] bg-background/50 font-mono text-sm border-border focus-visible:border-primary focus-visible:ring-primary/20 resize-none rounded-sm" {...field} />
                  </FormControl>
                  <FormDescription className="text-xs font-mono text-muted-foreground/70">
                    Specify if this is a new construction or expansion project.
                  </FormDescription>
                  <FormMessage />
                </FormItem>} />
          </TabsContent>
          
          <TabsContent value="refactoring" className="mt-0 space-y-4 force-mount">
            <FormField control={control} name="project_description" rules={{
            required: "Project details are required"
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel className="font-mono text-xs uppercase text-secondary">function audit_facility()</FormLabel>
                  <FormControl>
                    <Textarea placeholder="Detail your current compliance status, GISTM/ICMM goals, and specific oversight or monitoring requirements..." className="min-h-[120px] bg-background/50 font-mono text-sm border-border focus-visible:border-secondary focus-visible:ring-secondary/20 resize-none rounded-sm" {...field} />
                  </FormControl>
                  <FormDescription className="text-xs font-mono text-muted-foreground/70">
                    Include any existing IRTB or EOR documentation references.
                  </FormDescription>
                  <FormMessage />
                </FormItem>} />
          </TabsContent>
        </div>

        {}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <FormField control={control} name="budget" render={({
          field
        }) => <FormItem>
                <FormLabel className="font-mono text-xs uppercase text-muted-foreground">const capital_allocation</FormLabel>
                <FormControl>
                  <div className="relative">
                    <select className={cn("flex h-9 w-full items-center justify-between rounded-sm border border-input bg-background/50 px-3 py-2 text-sm shadow-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50 appearance-none font-mono", !field.value && "text-muted-foreground")} {...field}>
                      <option value="" disabled>Select range...</option>
                      <option value="Under $50,000">Under $50,000</option>
                      <option value="$50,000 - $250,000">$50,000 - $250,000</option>
                      <option value="$250,000 - $1,000,000">$250,000 - $1,000,000</option>
                      <option value="Over $1,000,000">Over $1,000,000</option>
                    </select>
                    <div className="absolute right-3 top-2.5 pointer-events-none opacity-50">
                      <Code className="size-4" />
                    </div>
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="timeline" render={({
          field
        }) => <FormItem>
                <FormLabel className="font-mono text-xs uppercase text-muted-foreground">const mobilization_deadline</FormLabel>
                <FormControl>
                  <div className="relative">
                    <select className={cn("flex h-9 w-full items-center justify-between rounded-sm border border-input bg-background/50 px-3 py-2 text-sm shadow-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50 appearance-none font-mono", !field.value && "text-muted-foreground")} {...field}>
                      <option value="" disabled>Select timeframe...</option>
                      <option value="Immediate (1 month)">Immediate (1 month)</option>
                      <option value="Short-term (3-6 months)">Short-term (3-6 months)</option>
                      <option value="Long-term (6-12 months)">Long-term (6-12 months)</option>
                      <option value="Strategic (12+ months)">Strategic (12+ months)</option>
                    </select>
                    <div className="absolute right-3 top-2.5 pointer-events-none opacity-50">
                      <Code className="size-4" />
                    </div>
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>} />
        </div>
      </Tabs>

      {submitError && <div className="p-3 bg-destructive/10 border border-destructive/30 rounded-sm text-destructive text-sm flex items-center gap-2">
          <CircleAlert className="size-4" />
          <span>{submitError}</span>
        </div>}

      <Button type="submit" disabled={isSubmitting} className={cn("w-full h-12 font-mono uppercase tracking-widest text-sm transition-all duration-300 rounded-sm", activeTab === "new-build" ? "bg-primary text-primary-foreground hover:bg-primary/90 shadow-[0_0_20px_-5px_var(--color-primary)]" : "bg-secondary text-secondary-foreground hover:bg-secondary/90 shadow-[0_0_20px_-5px_var(--color-secondary)]")}>
        {isSubmitting ? <>
            <Loader className="mr-2 size-4 animate-spin" />
            PROCESSING_DATA...
          </> : <>
            INITIATE_CONSULTATION <ArrowRight className="ml-2 size-4" />
          </>}
      </Button>
    </Form>;
};
export default function StartYourProject() {
  return <section id="start-your-consultation" className="dark bg-background text-foreground py-20 md:py-32 relative overflow-hidden">
      {}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)] pointer-events-none" />
      
      <div className="container mx-auto px-4 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8">
          
          {}
          <div className="lg:col-span-5 flex flex-col gap-8">
            <div className="space-y-4">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-sm bg-primary/10 border border-primary/20 text-primary text-xs font-mono uppercase tracking-wider">
                <Terminal className="size-3" />
                <span>Consultation Status: Open</span>
              </div>
              <h2 className="text-4xl md:text-5xl font-bold tracking-tighter text-foreground">
                Technical <span className="text-primary">Consultation</span>
              </h2>
              <p className="text-muted-foreground text-lg max-w-md">Direct access to our geotechnical and tailings experts. Initiate a connection for inspection, DSR, third party assessment, EOR inquiries, GISTM compliance, or operational program development.</p>
            </div>

            {}
            <div className="bg-card border border-border rounded-sm p-6 space-y-6 shadow-md relative overflow-hidden group">
              <div className="absolute top-0 left-0 w-1 h-full bg-primary/50 group-hover:bg-primary transition-colors" />
              
              <div className="space-y-4 font-mono text-sm">
                <div className="flex flex-col gap-1">
                  <div className="flex items-center gap-3 text-foreground hover:text-primary transition-colors">
                    <Mail className="size-4 text-primary" />
                    <Link to="mailto:consult@agt-engineering.com" className="break-all"> email <span className="text-primary" data-ast-id="f:/src/pages/Contact/sections/StartYourProject.tsx|sig:p8yc2k" data-ast-start="13969" data-ast-end="14029">williamwrupp@gmail.com</span></Link>
                  </div>
                </div>

                <div className="flex flex-col gap-1">
                  <div className="flex items-center gap-3 text-foreground hover:text-primary transition-colors">
                    <Phone className="size-4 text-primary" />
                    <Link to="tel:+15551234567" className="break-all"> phone <span className="text-primary">+1 (813) 774 2244</span></Link>
                  </div>
                </div>

                <div className="flex flex-col gap-1">
                  <div className="flex items-center gap-3 text-foreground">
                    <MapPin className="size-4 text-primary" />
                    <div>
                       location <span className="text-primary">Florida USA&nbsp;</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {}
            <div className="relative w-full aspect-video lg:aspect-square rounded-sm border border-border bg-muted/20 overflow-hidden group">
              {}
              <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.05)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.05)_1px,transparent_1px)] bg-[size:20px_20px]" />
              
              {}
              <div className="absolute inset-0 bg-cover bg-center bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1618705/img/map_dark_mode_grid.png)] opacity-40 mix-blend-luminosity group-hover:opacity-60 transition-opacity duration-500" />
              
              {}
              <div className="absolute inset-0 bg-[conic-gradient(from_0deg_at_50%_50%,transparent_0deg,transparent_60deg,rgba(var(--color-primary),0.1)_360deg)] animate-[spin_4s_linear_infinite] opacity-0 group-hover:opacity-100 transition-opacity" />
              
              {}
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex flex-col items-center gap-2">
                <div className="relative">
                  <div className="size-4 bg-primary rounded-sm shadow-[0_0_15px_var(--color-primary)] animate-pulse" />
                  <div className="absolute inset-0 size-4 bg-primary rounded-sm animate-ping opacity-50" />
                </div>
                <div className="bg-background/90 backdrop-blur-sm px-2 py-1 rounded-sm border border-primary/30 text-[10px] font-mono text-primary uppercase tracking-wider">
                  AGT_NODE_01
                </div>
              </div>

              {}
              <div className="absolute bottom-4 left-4 font-mono text-[10px] text-primary/70 bg-background/80 px-2 py-1 rounded-sm border border-primary/20">
                GLOBAL REACH <br />
                TAILINGS OVERSIGHT
              </div>
            </div>
          </div>

          {}
          <div className="lg:col-span-7">
            <div className="bg-card/50 backdrop-blur-sm border border-border rounded-sm p-6 md:p-8 shadow-2xl relative">
              {}
              <div className="absolute top-0 left-0 w-8 h-8 border-t-2 border-l-2 border-primary rounded-tl-sm" />
              <div className="absolute top-0 right-0 w-8 h-8 border-t-2 border-r-2 border-primary rounded-tr-sm" />
              <div className="absolute bottom-0 left-0 w-8 h-8 border-b-2 border-l-2 border-primary rounded-bl-sm" />
              <div className="absolute bottom-0 right-0 w-8 h-8 border-b-2 border-r-2 border-primary rounded-br-sm" />

              <div className="mb-8 flex items-center gap-3">
                <div className="p-2 bg-primary/10 rounded-sm border border-primary/20">
                  <Cpu className="size-6 text-primary" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-foreground">Project Brief</h3>
                  <p className="text-sm text-muted-foreground">Initialize geotechnical parameters.</p>
                </div>
              </div>

              <FormProvider formKey="consultation_form_contact" formId="consultation_form_contact" sectionName="start-your-consultation" defaultValues={{
              name: "",
              email: "",
              phone: "",
              company: "",
              project_description: "",
              budget: "",
              timeline: ""
            }} validationRules={{
              name: {
                required: true
              },
              email: {
                required: true,
                pattern: "email"
              },
              project_description: {
                required: true
              }
            }} submitText="INITIATE_CONSULTATION" successMessage="Engineering parameters received. Our team will analyze the data and establish a connection shortly." errorMessage="Transmission failed. Please verify your connection and try again.">
                <ContactForm />
              </FormProvider>
            </div>
          </div>
        </div>
      </div>
    </section>;
}